export type CredentialResponseDTO = {
	access_token: string;
	refresh_token: string;
	id_token: string | null;
};

const safeJsonParse = (value: string): unknown => {
	try {
		return JSON.parse(value);
	} catch {
		return null;
	}
};

const readStringField = (source: Record<string, unknown>, field: string): string | null => {
	const value = source[field];
	return typeof value === 'string' && value.length > 0 ? value : null;
};

/**
 * `/login/switch-credentials` is declared as returning `str` in the backend schema
 * while actually carrying the token object, so the body reaches us either as the
 * object itself or as a JSON string wrapping it. Both are accepted rather than
 * betting on one — the declared and the observed contract disagree.
 *
 * Throwing on an unrecognized shape is the whole point. Destructuring a bad body
 * yields `undefined` tokens that `saveAcquiredCredential` caches happily, after
 * which `selectIsAuthenticated` still reports true (`undefined !== null`), the
 * request interceptor silently omits the `Authorization` header, and the entry is
 * dropped on persist because `isPersistedEntry` demands a string. That failure
 * surfaces as "everything 401s and the session vanishes on reload", nowhere near
 * its cause — this is the checkpoint that turns it into one legible error.
 */
export const parseCredentialResponse = (body: unknown): CredentialResponseDTO => {
	const payload: unknown = typeof body === 'string' ? safeJsonParse(body) : body;

	if (typeof payload !== 'object' || payload === null) {
		throw new Error('Credential response is not a token object');
	}

	const source = payload as Record<string, unknown>;
	const accessToken = readStringField(source, 'access_token');
	const refreshToken = readStringField(source, 'refresh_token');

	if (!accessToken || !refreshToken) {
		throw new Error('Credential response is missing access_token or refresh_token');
	}

	return {
		access_token: accessToken,
		refresh_token: refreshToken,
		id_token: readStringField(source, 'id_token'),
	};
};
